import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Table, Form, InputGroup, Row, Col, Badge, Alert } from 'react-bootstrap';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import AddTimeEntryModal from './AddTimeEntryModal';

const TimeEntries = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useContext(AuthContext);
  
  // Get employeeId from query params if available
  const queryParams = new URLSearchParams(location.search);
  const employeeIdFromQuery = queryParams.get('employeeId');
  
  const [project, setProject] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [laborSummary, setLaborSummary] = useState(null);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // State for filtering
  const [filters, setFilters] = useState({
    employeeId: employeeIdFromQuery || '',
    startDate: '',
    endDate: '',
    searchTerm: ''
  });
  
  // State for time entry modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  // State for date range
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [dateRange, setDateRange] = useState({
    start: firstDayOfMonth.toISOString().split('T')[0],
    end: today.toISOString().split('T')[0]
  });
  
  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch project details
        const projectResponse = await fetch(`/api/projects/${projectId}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!projectResponse.ok) {
          throw new Error('Failed to fetch project details');
        }
        
        const projectData = await projectResponse.json();
        setProject(projectData);
        
        // Fetch employees assigned to this project
        const employeesResponse = await fetch(`/api/employees/project/${projectId}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (employeesResponse.ok) {
          const employeesData = await employeesResponse.json();
          setEmployees(employeesData);
        }
        
        // Fetch time entries
        const timeEntriesResponse = await fetch(`/api/time-entries/project/${projectId}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (timeEntriesResponse.ok) {
          const timeEntriesData = await timeEntriesResponse.json();
          setTimeEntries(timeEntriesData);
          setFilteredEntries(timeEntriesData);
        }
        
        // Fetch labor summary
        const laborSummaryResponse = await fetch(`/api/time-entries/project/${projectId}/summary`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (laborSummaryResponse.ok) {
          const laborSummaryData = await laborSummaryResponse.json();
          setLaborSummary(laborSummaryData);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load project data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [projectId, token]);
  
  // Filter time entries based on user selections
  useEffect(() => {
    if (timeEntries.length === 0) return;
    
    let filtered = [...timeEntries];
    
    // Filter by employee
    if (filters.employeeId) {
      filtered = filtered.filter(entry => entry.EmployeeId.toString() === filters.employeeId);
    }
    
    // Filter by date range
    if (dateRange.start) {
      filtered = filtered.filter(entry => {
        const entryDate = new Date(entry.Date).toISOString().split('T')[0];
        return entryDate >= dateRange.start;
      });
    }
    
    if (dateRange.end) {
      filtered = filtered.filter(entry => {
        const entryDate = new Date(entry.Date).toISOString().split('T')[0];
        return entryDate <= dateRange.end;
      });
    }
    
    // Filter by search term
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(entry => 
        (entry.FirstName && entry.FirstName.toLowerCase().includes(term)) ||
        (entry.LastName && entry.LastName.toLowerCase().includes(term)) ||
        (entry.Notes && entry.Notes.toLowerCase().includes(term))
      );
    }
    
    setFilteredEntries(filtered);
  }, [timeEntries, filters, dateRange]);
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  
  // Handle date range changes
  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange({
      ...dateRange,
      [name]: value
    });
  };
  
  // Open modal for adding a new time entry
  const openAddModal = () => {
    setSelectedEntry(null);
    setShowAddModal(true);
  };
  
  // Open modal for editing a time entry
  const openEditModal = (entry) => {
    setSelectedEntry(entry);
    setShowAddModal(true);
  };
  
  // Handle save time entry (create or update)
  const handleSaveTimeEntry = async (formData) => {
    try {
      let response;
      
      // Format the date
      const formattedDate = new Date(formData.date).toISOString().split('T')[0];
      
      if (selectedEntry) {
        // Update existing time entry
        response = await fetch(`/api/time-entries/${selectedEntry.TimeEntryId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          },
          body: JSON.stringify({
            date: formattedDate,
            regularHours: formData.regularHours,
            overtimeHours: formData.overtimeHours,
            notes: formData.notes
          })
        });
      } else {
        // Create new time entry
        response = await fetch('/api/time-entries', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          },
          body: JSON.stringify({
            projectId,
            employeeId: formData.employeeId,
            date: formattedDate,
            regularHours: formData.regularHours,
            overtimeHours: formData.overtimeHours,
            notes: formData.notes
          })
        });
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || 'Failed to save time entry');
      }
      
      // Refresh data
      const timeEntriesResponse = await fetch(`/api/time-entries/project/${projectId}`, {
        headers: {
          'x-auth-token': token
        }
      });
      
      if (timeEntriesResponse.ok) {
        const timeEntriesData = await timeEntriesResponse.json();
        setTimeEntries(timeEntriesData);
      }
      
      // Refresh labor summary
      const laborSummaryResponse = await fetch(`/api/time-entries/project/${projectId}/summary`, {
        headers: {
          'x-auth-token': token
        }
      });
      
      if (laborSummaryResponse.ok) {
        const laborSummaryData = await laborSummaryResponse.json();
        setLaborSummary(laborSummaryData);
      }
      
      toast.success(`Time entry ${selectedEntry ? 'updated' : 'added'} successfully`);
      setShowAddModal(false);
    } catch (err) {
      console.error('Error saving time entry:', err);
      toast.error(err.message || 'Failed to save time entry');
    }
  };
  
  // Handle delete time entry
  const handleDeleteTimeEntry = async (timeEntryId) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/time-entries/${timeEntryId}`, {
        method: 'DELETE',
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete time entry');
      }
      
      // Remove from state
      setTimeEntries(timeEntries.filter(entry => entry.TimeEntryId !== timeEntryId));
      
      // Refresh labor summary
      const laborSummaryResponse = await fetch(`/api/time-entries/project/${projectId}/summary`, {
        headers: {
          'x-auth-token': token
        }
      });
      
      if (laborSummaryResponse.ok) {
        const laborSummaryData = await laborSummaryResponse.json();
        setLaborSummary(laborSummaryData);
      }
      
      toast.success('Time entry deleted successfully');
    } catch (err) {
      console.error('Error deleting time entry:', err);
      toast.error('Failed to delete time entry');
    }
  };
  
  // Format currency 
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${parseFloat(value).toFixed(2)}`;
  };
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading time entries...</div>;
  }
  
  if (error) {
    return (
      <Alert variant="danger" className="mt-4">
        {error}
        <div className="mt-3">
          <Button onClick={() => navigate('/projects')} variant="primary">
            Back to Projects
          </Button>
        </div>
      </Alert>
    );
  }
  
  if (!project) {
    return (
      <Alert variant="warning" className="mt-4">
        Project not found
        <div className="mt-3">
          <Button onClick={() => navigate('/projects')} variant="primary">
            Back to Projects
          </Button>
        </div>
      </Alert>
    );
  }
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Time Entries</h2>
          <h5 className="text-muted">
            Project: <Link to={`/projects/${projectId}`}>{project.ProjectName}</Link>
          </h5>
        </div>
        <div>
          <Button
            variant="primary"
            onClick={openAddModal}
            disabled={employees.length === 0}
          >
            Add Time Entry
          </Button>
        </div>
      </div>
      
      {/* Labor Cost Summary Card */}
      {laborSummary && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Labor Summary</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <h6>Regular Hours</h6>
                  <h4>{laborSummary.projectTotal.TotalRegularHours?.toFixed(2) || '0.00'}</h4>
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <h6>Overtime Hours</h6>
                  <h4>{laborSummary.projectTotal.TotalOvertimeHours?.toFixed(2) || '0.00'}</h4>
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <h6>Total Hours</h6>
                  <h4>{laborSummary.projectTotal.TotalHours?.toFixed(2) || '0.00'}</h4>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <div>
                  <h6>Regular Labor Cost</h6>
                  <h4>{formatCurrency(laborSummary.projectTotal.RegularCost)}</h4>
                </div>
              </Col>
              <Col md={4}>
                <div>
                  <h6>Overtime Labor Cost</h6>
                  <h4>{formatCurrency(laborSummary.projectTotal.OvertimeCost)}</h4>
                </div>
              </Col>
              <Col md={4}>
                <div>
                  <h6>Total Labor Cost</h6>
                  <h4 className="text-primary">{formatCurrency(laborSummary.projectTotal.TotalCost)}</h4>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
      
      {/* Filters Card */}
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">Filters</h5>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Employee</Form.Label>
                <Form.Select
                  name="employeeId"
                  value={filters.employeeId}
                  onChange={handleFilterChange}
                >
                  <option value="">All Employees</option>
                  {employees.filter(emp => emp.IsActive !== false).map(emp => (
                    <option key={emp.EmployeeId} value={emp.EmployeeId}>
                      {emp.FirstName} {emp.LastName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  name="start"
                  value={dateRange.start}
                  onChange={handleDateRangeChange}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  name="end"
                  value={dateRange.end}
                  onChange={handleDateRangeChange}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <InputGroup>
                  <Form.Control
                    placeholder="Search..."
                    name="searchTerm"
                    value={filters.searchTerm}
                    onChange={handleFilterChange}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {/* Time Entries Table */}
      <Card>
        <Card.Body>
          {employees.length === 0 ? (
            <Alert variant="info">
              <p>No employees are assigned to this project yet.</p>
              <Link to={`/projects/${projectId}/assign-employees`}>
                <Button variant="primary">Assign Employees</Button>
              </Link>
            </Alert>
          ) : filteredEntries.length > 0 ? (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Employee</th>
                    <th>Regular Hours</th>
                    <th>Overtime Hours</th>
                    <th>Total Hours</th>
                    <th>Labor Cost</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map(entry => {
                    const regularCost = parseFloat(entry.RegularHours) * parseFloat(entry.HourlyWage);
                    const overtimeCost = parseFloat(entry.OvertimeHours || 0) * parseFloat(entry.HourlyWage) * 1.5;
                    const totalCost = regularCost + overtimeCost;
                    const totalHours = parseFloat(entry.RegularHours) + parseFloat(entry.OvertimeHours || 0);
                    
                    return (
                      <tr key={entry.TimeEntryId}>
                        <td>{new Date(entry.Date).toLocaleDateString()}</td>
                        <td>
                          <Link to={`/employees/${entry.EmployeeId}`}>
                            {entry.FirstName} {entry.LastName}
                          </Link>
                          <Badge 
                            bg="secondary" 
                            className="ms-2"
                          >
                            {formatCurrency(entry.HourlyWage)}/hr
                          </Badge>
                        </td>
                        <td>{parseFloat(entry.RegularHours).toFixed(2)}</td>
                        <td>{parseFloat(entry.OvertimeHours || 0).toFixed(2)}</td>
                        <td>{totalHours.toFixed(2)}</td>
                        <td>{formatCurrency(totalCost)}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => openEditModal(entry)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteTimeEntry(entry.TimeEntryId)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-4">
              <p>No time entries found matching your filters.</p>
            </div>
          )}
        </Card.Body>
      </Card>
      
      {/* Time Entry Modal */}
      <AddTimeEntryModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSave={handleSaveTimeEntry}
        employees={employees.filter(emp => emp.IsActive !== false)}
        timeEntry={selectedEntry}
        isEditing={!!selectedEntry}
      />
    </div>
  );
};

export default TimeEntries;
